import React, { createContext, useEffect, useMemo, useState } from "react";

import { Dialog, CircularProgress, DialogTitle, FormControl, FormControlLabel, DialogActions, Button, Switch, makeStyles } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import ChatIcon from '@material-ui/icons/ChatBubble';
import PeopleIcon from '@material-ui/icons/People';

import { CommentsCount } from "react-facebook";
import { useIsMobile } from "../Mui3WidthProvider";
import { ShareItem } from "../../entities/ShareItem";
import UserSharePreference from "../../entities/UserSharePreference";
import { useAuthenticatedUser } from "../../utilities/useAuthenticatedUser";
import { ShareService } from "../../services/ShareService";
import { RenderNonPaidUnshareDialog } from "./RenderNonPaidUnshareDialog";
import { SubdomainSetupDialog } from "./SubdomainSetupDialog";
import { RenderUnsharedContent } from "./RenderUnsharedContent";
import { RenderSharedContent } from "./RenderSharedContent";

const useStyles = makeStyles((theme) => ({
    container: {
        minWidth: '600px',
        [theme.breakpoints.down('sm')]: {
            minWidth: 'initial'
        },
    },
    closeMobileIcon: {
        color: "rgb(0, 0, 0, .4)",
        position: "absolute",
        top: 10,
        right: 10
    },
    titleRight: {
        float: 'right',
        paddingTop: 5,
        [theme.breakpoints.down('sm')]: {
            float: 'initial'
        },
    },
    mobileFormLabel: {
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0
        },
    },
    switchLabel: {
        fontWeight: 500,
        fontSize: 16
    },
    loadingContainer: {
        height: 375,
        width: 600,
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            width: 'auto'
        },
    },
}));

interface SharingDialogContext {
    sharedItem: ShareItem;
    sharedUrl: string;
    updateSharedItem: (item:ShareItem) => void;
    onError: (message:string) => void;
    showMessage: (message:string) => void;
}

const ReactSharingDialogContext = createContext<SharingDialogContext>({} as SharingDialogContext);

export const useSharingDialog = () => React.useContext(ReactSharingDialogContext);

interface Props {
    onClose: () => void;
    isOpen: boolean;
    id: string;
    onError: (message: string) => void;
    onGiveMessage: (message: string) => void;
}

export function SharingDialog(props:Props){
    const isMobile = useIsMobile();
    const classes = useStyles();
    const user = useAuthenticatedUser();
    const [disabled, setDisabled] = useState(true);
    const [openUnshareNonPaidDialog, setOpenUnshareNonPaidDialog] = useState(false);
    const [firstTimeUser, setFirstTimeUser] = useState(false);
    const [sharedItem, setSharedItem] = useState<ShareItem>();
    const [sharePreferences, setSharePreferences] = useState<UserSharePreference>();

    const shareUrl = useMemo(() => `https://${sharePreferences?.subdomain}${sharedItem?.slug}`, [sharedItem, sharePreferences]);

    useEffect(() => {
        async function fetchData(){
            setDisabled(true);
            const sharePreferencesResponse = await ShareService.getUserSharePreference();
            setDisabled(false);

            if(sharePreferencesResponse.error){
                props.onError("Unable to initialize share. Please try again");
                return props.onClose();
            }

            if(!sharePreferencesResponse.data) return setFirstTimeUser(true);
            
            setSharePreferences(sharePreferencesResponse.data);

            setDisabled(true);
            const existingShareResponse = await ShareService.getUserShareItem(props.id);
            setDisabled(false);

            if(existingShareResponse.error){
                props.onError("Unable to get share. Please try again");
                return props.onClose();
            };

            if(existingShareResponse.data) return setSharedItem(existingShareResponse.data);

            setDisabled(true);
            const newShareResponse = await ShareService.createUserShareItem(props.id);
            setDisabled(false);

            if(newShareResponse.error) {
                props.onError("Unable to get share. Please try again");
                return props.onClose();
            }

            setSharedItem(newShareResponse.data)
        }

        fetchData();
    }, []);

    const handleSubdomainSetup = (item:ShareItem, updatedPreferences:UserSharePreference) => {
        setSharedItem(item);
        setSharePreferences(updatedPreferences);
        setFirstTimeUser(false);
    }

    const handleShareToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        if(!sharedItem) return;
        if (disabled) return;

        if(!user.makerSubscription) return setOpenUnshareNonPaidDialog(true);

        setDisabled(true);
        const response = await ShareService.saveAssetShare({
            commentsEnabled: sharedItem.commentsEnabled,
            enabled: e.currentTarget.checked,
            assetShareId: sharedItem.id,
            assetShareVideoTypeId: sharedItem.assetShareType,
            videoPath: sharedItem.videoPath
        });
        setDisabled(false);

        if(response.error) return props.onError('An unknown error occurred. Please try again.');

        setSharedItem(response.data);
        props.onGiveMessage(`Share is now ${response.data.enabled ? "enabled" : "disabled"}.`)
    }

    if (openUnshareNonPaidDialog && sharedItem) {
        return (
            <RenderNonPaidUnshareDialog 
                isOpen={openUnshareNonPaidDialog} 
                onClose={props.onClose} 
                sharedItem={sharedItem} 
                onError={props.onError} 
                handleUnshare={(item) => {setSharedItem(item); setOpenUnshareNonPaidDialog(false)}} 
            />
        )
    }

    if (firstTimeUser) {
        return (
            <SubdomainSetupDialog 
                isOpen={firstTimeUser}
                itemId={props.id} 
                onClose={props.onClose} 
                handleShareSuccess={handleSubdomainSetup} 
            />
        )
    }

    return (
        <Dialog
            open={props.isOpen}
            onClose={() => props.onClose()}
            fullScreen={isMobile}
        >
            <div className={classes.container}>
            { !sharedItem
            ?
                <div className={classes.loadingContainer}>
                    <CircularProgress style={{ marginTop: "150px" }} />
                </div>
            :
                <ReactSharingDialogContext.Provider 
                    value={{ 
                        sharedItem: sharedItem,
                        sharedUrl: shareUrl,
                        updateSharedItem: setSharedItem,
                        onError: props.onError,
                        showMessage: props.onGiveMessage
                    }}>
                    {isMobile && <CloseIcon className={classes.closeMobileIcon} onClick={props.onClose} />}
                    <DialogTitle id="form-dialog-title">
                        <div className={classes.titleRight}>
                            <div style={{ display: 'inline-block' }}>
                                <span
                                    onClick={() => sharedItem.enabled ? window.location.assign(shareUrl) : null}
                                    style={{
                                        cursor: `${sharedItem.enabled ? "pointer" : "initial"}`,
                                        paddingTop: 6,
                                        paddingBottom: 6,
                                        paddingLeft: 4,
                                        paddingRight: 4,
                                        color: "#FFF",
                                        backgroundColor: `${sharedItem.enabled ? "rgb(66, 133, 244)" : "#a7a7a7"}`,
                                        borderRadius: 4,
                                        verticalAlign: "super",
                                        display: 'inline-flex',
                                        marginLeft: 0,
                                    }}
                                >
                                    <PeopleIcon style={{ fontSize: 16, verticalAlign: 'bottom', marginRight: 3 }} /> <span style={{ fontSize: 13, marginTop: -2 }} >{sharedItem.views}</span>
                                </span>
                                <div
                                    style={{
                                        width: 0,
                                        height: 0,
                                        borderLeft: `${sharedItem.enabled ? "5px solid rgb(66, 133, 244)" : "5px solid #a7a7a7"}`,
                                        borderBottom: '5px solid transparent',
                                        borderTop: '5px solid transparent',
                                        verticalAlign: 'super',
                                        display: 'inline-block',
                                        marginBottom: 3
                                    }}
                                    />
                            </div>
                            <div style={{ display: 'inline-block' }}>
                                <span
                                    onClick={() => sharedItem.enabled ? window.location.assign(shareUrl) : null}
                                    style={{
                                        cursor: `${sharedItem.enabled ? "pointer" : "initial"}`,
                                        paddingTop: 6,
                                        paddingBottom: 6,
                                        paddingLeft: 4,
                                        paddingRight: 4,
                                        color: "#FFF",
                                        backgroundColor: `${sharedItem.enabled ? "rgb(66, 133, 244)" : "#a7a7a7"}`,
                                        borderRadius: 4,
                                        verticalAlign: "super",
                                        display: 'inline-flex',
                                        marginLeft: 8,
                                    }}
                                    >
                                    <ChatIcon style={{ transform: 'scaleX(-1)', fontSize: 16, verticalAlign: 'bottom', marginRight: 3 }} />
                                    <span style={{ fontSize: 13, marginTop: -2 }}>
                                        <CommentsCount href={shareUrl} />
                                    </span>

                                </span>
                                <div
                                    style={{
                                        width: 0,
                                        height: 0,
                                        borderLeft: `${sharedItem.enabled ? "5px solid rgb(66, 133, 244)" : "5px solid #a7a7a7"}`,
                                        borderBottom: '5px solid transparent',
                                        borderTop: '5px solid transparent',
                                        verticalAlign: 'super',
                                        display: 'inline-block',
                                        marginBottom: 3
                                    }}
                                    />
                            </div>

                            <FormControl>
                                <FormControlLabel
                                    label={`Sharing is ${sharedItem.enabled ? "on" : "off"}`}
                                    className={classes.mobileFormLabel}
                                    control={
                                        <Switch
                                        checked={sharedItem.enabled}
                                        onChange={handleShareToggle}
                                        value="Share"
                                            color="primary"
                                            />
                                        }
                                        classes={{ label: classes.switchLabel }}
                                        labelPlacement="start"
                                />
                            </FormControl>
                        </div>
                    </DialogTitle>
                    {sharedItem.enabled
                        ? <RenderSharedContent />
                        : <RenderUnsharedContent  />
                    }
                    <DialogActions>
                        <Button onClick={() => props.onClose()} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </ReactSharingDialogContext.Provider>
            }
            </div>
        </Dialog>
        );
}